import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  DiffResult,
  FlowDetail,
  FlowMutationResult,
  FlowSummary,
  IrView,
  PublishRequest,
  PublishResult,
  ValidationResult,
} from './types';

/**
 * Product-API flow endpoints (the PROPOSED OpenAPI contract). Every call unwraps the
 * `{status, traceId, data}` envelope and returns the typed `data`. Mutating POSTs carry an
 * `ol-idempotency-key`. Callers handle failures via `normalizeApiError` (errors.ts).
 */

const enc = (s: string) => encodeURIComponent(s);

/** GET /v1/flows — the hub's flows (latest version of each). */
export async function listFlows(includeArchived = false): Promise<FlowSummary[]> {
  const { data } = await apiClient.get<ApiEnvelope<FlowSummary[]>>('/v1/flows', {
    params: includeArchived ? { includeArchived: true } : undefined,
  });
  return unwrap(data);
}

/** POST /v1/flows/{id}/archive — reversible estate hygiene; nothing is deleted. */
export async function archiveFlow(id: string, note?: string): Promise<FlowSummary> {
  const { data } = await apiClient.post<ApiEnvelope<FlowSummary>>(
    `/v1/flows/${enc(id)}/archive`,
    { note: note ?? null },
    idempotentConfig(),
  );
  return unwrap(data);
}

/** POST /v1/flows/{id}/unarchive — restore an archived flow exactly as it was. */
export async function unarchiveFlow(id: string): Promise<FlowSummary> {
  const { data } = await apiClient.post<ApiEnvelope<FlowSummary>>(
    `/v1/flows/${enc(id)}/unarchive`,
    {},
    idempotentConfig(),
  );
  return unwrap(data);
}

/** GET /v1/flows/{id} — versions + publish state. */
export async function getFlowDetail(id: string): Promise<FlowDetail> {
  const { data } = await apiClient.get<ApiEnvelope<FlowDetail>>(`/v1/flows/${enc(id)}`);
  return unwrap(data);
}

/** GET /v1/flows/{id}/ir — the canonical IR (YAML authoring form by default). */
export async function getFlowIr(
  id: string,
  opts: { version?: number; form?: 'yaml' | 'json' } = {},
): Promise<IrView> {
  const { data } = await apiClient.get<ApiEnvelope<IrView>>(`/v1/flows/${enc(id)}/ir`, {
    params: { version: opts.version, form: opts.form ?? 'yaml' },
  });
  return unwrap(data);
}

/** POST /v1/flows — create flow v1 from an IR authoring form. */
export async function createFlow(
  source: string,
  format: 'yaml' | 'json' = 'yaml',
  idemKey?: string,
): Promise<FlowMutationResult> {
  const { data } = await apiClient.post<ApiEnvelope<FlowMutationResult>>(
    '/v1/flows',
    { format, source },
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** POST /v1/flows/{id}/versions — new draft version of an existing flow. */
export async function createFlowVersion(
  id: string,
  source: string,
  format: 'yaml' | 'json' = 'yaml',
  idemKey?: string,
): Promise<FlowMutationResult> {
  const { data } = await apiClient.post<ApiEnvelope<FlowMutationResult>>(
    `/v1/flows/${enc(id)}/versions`,
    { format, source },
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** POST /v1/flows/{id}/validate — compile + analyse, no store (inline diagnostics). */
export async function validateFlow(
  id: string,
  source: string,
  format: 'yaml' | 'json' = 'yaml',
): Promise<ValidationResult> {
  const { data } = await apiClient.post<ApiEnvelope<ValidationResult>>(
    `/v1/flows/${enc(id)}/validate`,
    { format, source },
  );
  return unwrap(data);
}

/** POST /v1/flows/{id}/diff?from&to — semantic diff between two stored versions. */
export async function diffFlow(id: string, from: number, to: number): Promise<DiffResult> {
  const { data } = await apiClient.post<ApiEnvelope<DiffResult>>(
    `/v1/flows/${enc(id)}/diff`,
    undefined,
    { params: { from, to } },
  );
  return unwrap(data);
}

/** POST /v1/flows/{id}/publish — sign + activate a version (approvalId per the PS-2 gate). */
export async function publishFlow(
  id: string,
  body: PublishRequest,
  idemKey?: string,
): Promise<PublishResult> {
  const { data } = await apiClient.post<ApiEnvelope<PublishResult>>(
    `/v1/flows/${enc(id)}/publish`,
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}
